/**
 * In-grid EDC knowledge cards (merchant 2026-10-30; pattern: Tactile Turn's review tile inside the product grid).
 * - Desktop only for now (>= 1024px); hidden in list view by CSS.
 * - Random: after every 10-15 products one card is dropped at a random slot of that stretch (never in the first
 *   row, never next to another card), with a random glossary term that does not repeat on the page.
 * - Display only: no links, so the card never pulls shoppers out of the buying flow.
 * - Survives filtering/sorting (grid swapped) and infinite scroll (products appended).
 */
const dataEl = document.getElementById('fz-knowledge-data');
const desktop = window.matchMedia('(min-width: 64rem)');
const KC_CACHE = 'fz-knowledge-data-v1';

// Speed pass (2026-10-31): the term list comes from a small JSON endpoint, fetched once per session and cached, and only
// on desktop where the cards show, instead of being rebuilt inside every collection render.
async function loadKnowledge() {
  try {
    const cached = sessionStorage.getItem(KC_CACHE);
    if (cached) return JSON.parse(cached);
  } catch (e) { /* storage blocked */ }
  if (dataEl.textContent.trim()) {
    try { return JSON.parse(dataEl.textContent); } catch (e) { /* fall through */ }
  }
  if (!dataEl.dataset.src) return null;
  try {
    const res = await fetch(dataEl.dataset.src, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const text = await res.text();
    const data = JSON.parse(text);
    try { sessionStorage.setItem(KC_CACHE, text); } catch (e) { /* storage full */ }
    return data;
  } catch (e) {
    return null;
  }
}

function startKnowledge(data) {
  if (data && Array.isArray(data.terms) && data.terms.length) {
    data.terms.forEach((term, i) => { term.n = i + 1; });
    const total = data.terms.length;
    const pool = shuffle(data.terms.slice());
    let poolIndex = 0;
    const nextTerm = () => {
      if (poolIndex >= pool.length) { shuffle(pool); poolIndex = 0; }
      return pool[poolIndex++];
    };
    const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

    const process = () => {
      const grid = document.getElementById('product-grid');
      if (!grid || !desktop.matches) return;
      if (!grid.dataset.kcInit) {
        grid.dataset.kcInit = '1';
        grid.kc = { count: 0, target: randInt(6, 12), lastWasCard: false };
      }
      const state = grid.kc;
      const items = grid.querySelectorAll(':scope > li:not(.kc-card):not([data-kc-seen])');
      items.forEach((li) => {
        li.dataset.kcSeen = '1';
        state.count += 1;
        state.lastWasCard = false;
        if (state.count >= state.target && li.nextElementSibling && !li.nextElementSibling.classList.contains('kc-card')) {
          li.after(buildCard(nextTerm()));
          state.lastWasCard = true;
          state.count = 0;
          state.target = randInt(10, 15);
        }
      });
    };

    function buildCard(term) {
      const li = document.createElement('li');
      li.className = 'kc-card';
      const card = document.createElement('aside');
      card.className = 'kc-card__inner';
      card.setAttribute('aria-label', `${data.label}: ${term.t}`);
      const pad = (n) => String(n).padStart(2, '0');
      card.innerHTML = `
        <div class="kc-card__head">
          ${data.logo ? `<img class="kc-card__logo" src="${data.logo}" alt="" width="36" height="36" loading="lazy">` : ''}
          <div class="kc-card__brand"><span class="kc-card__studio"></span><span class="kc-card__label"></span></div>
          <div class="kc-card__page">
            <svg class="kc-card__book" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6.5C10.2 5.2 7.6 4.5 3.5 4.5v13c4.1 0 6.7.7 8.5 2 1.8-1.3 4.4-2 8.5-2v-13c-4.1 0-6.7.7-8.5 2z"/><path d="M12 6.5V19.5"/><path d="M15 9.5c1.2-.5 2.6-.8 3.5-.8M15 12.5c1.2-.5 2.6-.8 3.5-.8"/></svg>
            <span class="kc-card__no">N&ordm; ${pad(term.n)}<span class="kc-card__of"> / ${pad(total)}</span></span>
          </div>
        </div>
        <div class="kc-card__body">
          <p class="kc-card__cat"></p>
          <p class="kc-card__term"></p>
          ${term.f ? '<p class="kc-card__full"></p>' : ''}
          <p class="kc-card__desc"></p>
        </div>
        <p class="kc-card__foot"><svg class="kc-card__foot-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 4.5h11a3 3 0 0 1 3 3v12H8a3 3 0 0 1-3-3z"/><path d="M5 16.5a3 3 0 0 1 3-3h11"/></svg><span></span></p>`;
      card.querySelector('.kc-card__studio').textContent = data.studio || '';
      card.querySelector('.kc-card__label').textContent = data.label;
      card.querySelector('.kc-card__cat').textContent = term.c;
      card.querySelector('.kc-card__term').textContent = term.t;
      if (term.f) card.querySelector('.kc-card__full').textContent = term.f;
      card.querySelector('.kc-card__desc').textContent = term.d;
      card.querySelector('.kc-card__foot span').textContent = data.footer;
      li.append(card);
      return li;
    }

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; process(); });
    };
    process();
    const root = document.querySelector('[data-collection-layout]') || document.body;
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    desktop.addEventListener('change', schedule);
  }
}

function shuffle(list) {
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

if (dataEl) {
  const boot = () => loadKnowledge().then((data) => { if (data) startKnowledge(data); });
  if (desktop.matches) {
    boot();
  } else {
    const once = (e) => { if (e.matches) { desktop.removeEventListener('change', once); boot(); } };
    desktop.addEventListener('change', once);
  }
}
